import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useNotify, useTranslate } from 'ra-core';
import { useForwardMessage } from '../../../../api/hooks/sendbird/useForwardMessage';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import {
  FORWARD_MESSAGE_CHANNEL_LIMIT,
  useForwardMessageContext,
} from '../../ForwardMessageProvider';
import ForwardChannelCard from './ForwardChannelCard';

export const ForwardMessageModal = () => {
  const notify = useNotify();
  const translate = useTranslate();
  const shareMessageContext = useForwardMessageContext();
  const [selectedChannelsUrls, setSelectedChannelsUrls] = useState<string[]>(
    []
  );
  const { message, groupChannels } = shareMessageContext;
  const { mutate: forwardMessage, isPending } = useForwardMessage();

  const handleClose = () => {
    shareMessageContext.setShowModal(false);
  };

  const handleToggle = (url: string) => {
    setSelectedChannelsUrls((selected) =>
      selected.includes(url)
        ? selected.filter((selectedUrl) => selectedUrl !== url)
        : [...selected, url]
    );
  };

  const handleShare = () => {
    const groupsToShare = groupChannels?.filter((channel) =>
      selectedChannelsUrls.includes(channel.url)
    );
    if (!groupsToShare?.length || !message) return;

    forwardMessage(
      { message, channels: groupsToShare },
      {
        onSuccess: () => {
          setSelectedChannelsUrls([]);
          handleClose();
          notify(translate('chat.messageForwarded'), {
            type: 'success',
            autoHideDuration: 5000,
          });
        },
      }
    );
  };

  return (
    <Dialog
      open={shareMessageContext.showModal}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{translate('chat.messageForwardTitle')}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto border-y">
          {groupChannels?.map((channel) => {
            if (channel.url === message?.channelUrl) return null;

            return (
              <ForwardChannelCard
                channel={channel}
                selectedChannelsUrls={selectedChannelsUrls}
                isPending={isPending}
                onClick={() => handleToggle(channel.url)}
                key={channel.url}
              />
            );
          })}
        </div>
        <DialogFooter className="px-6 py-4">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isPending}
          >
            {translate('chat.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleShare}
            disabled={!selectedChannelsUrls.length || isPending}
          >
            {isPending && <Loader2 className="animate-spin" />}
            {translate('chat.forward')}{' '}
            {selectedChannelsUrls.length > 0
              ? `(${selectedChannelsUrls.length}/${FORWARD_MESSAGE_CHANNEL_LIMIT})`
              : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
